// =============================================================================
// SHANIZ GAMING ADMIN - API SERVICE
// =============================================================================

import axios from 'axios';
import type {
  DashboardStats,
  GameSession,
  GameStats,
  LeaderboardEntry,
  PaginatedResponse,
  RevenueData,
  Tournament,
  Transaction,
  User,
  GameType,
  Report,
  AntiCheatEvent,
  AdminAction,
} from '../types';

// API Configuration
// Default to the production API. DevOps can still override via REACT_APP_API_URL
// at build time; the fallback must be the real backend, not a stale IP.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.shanzz.com/api/v1';

// -----------------------------------------------------------------------------
// Defensive helpers for the paginated shapes the backend emits.
//
// The shaniz backend is inconsistent about the key it uses for the row array —
// different endpoints respond with `items`, `data`, `users`, `logs`, `actions`,
// `events`, `reports`, `sessions`, `kyc`, `tickets`, `runs`, `tournaments`.
// These helpers hide that drift from page components so we never hit a crash
// like `.map is not a function` when a new endpoint returns a slightly
// different envelope.
// -----------------------------------------------------------------------------

const LIST_KEYS = [
  'items', 'data', 'users', 'logs', 'actions', 'events', 'reports',
  'sessions', 'kyc', 'tickets', 'runs', 'tournaments', 'payouts', 'submissions',
  'leaderboard', 'records', 'results',
] as const;

export function extractList<T = unknown>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  if (!body || typeof body !== 'object') return [];
  const obj = body as Record<string, unknown>;
  for (const k of LIST_KEYS) {
    if (Array.isArray(obj[k])) return obj[k] as T[];
  }
  return [];
}

export function extractTotal(body: unknown, fallback = 0): number {
  if (!body || typeof body !== 'object') return fallback;
  const obj = body as Record<string, any>;
  return (
    obj.pagination?.total ??
    obj.total ??
    fallback
  );
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// =============================================================================
// AUTH
// =============================================================================

export const authService = {
  login: async (email: string, password: string) => {
    // Admin login with email and password at /auth/admin/login
    const response = await api.post('/auth/admin/login', { email, password });
    // Response format: { success, data: { token, refreshToken, user } }
    const data = response.data.data || response.data;
    return {
      token: data.token || data.accessToken,
      user: {
        id: data.user.id,
        email: data.user.email || data.user.username,
        name: data.user.username,
        role: data.user.role,
      }
    };
  },

  logout: () => {
    localStorage.removeItem('admin_token');
  },

  getCurrentUser: async () => {
    const response = await api.get('/admin/me');
    const data = response.data.data || response.data;
    return {
      id: data.id,
      email: data.email || data.username,
      name: data.username,
      role: data.role,
    };
  },
};

// =============================================================================
// DASHBOARD
// =============================================================================

export const dashboardService = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/admin/dashboard');
    const data = response.data.data || response.data;
    // Map backend response to frontend expected format
    return {
      totalUsers: data.users?.total || 0,
      activeUsers: data.users?.active || 0,
      totalGames: data.sessions?.total || 0,
      gamesInProgress: data.sessions?.active || 0,
      totalRevenue: data.transactions?.total || 0,
      todayRevenue: data.sessions?.today || 0,
      totalTournaments: data.tournaments?.total || 0,
      activeTournaments: data.tournaments?.active || 0,
    } as DashboardStats;
  },

  getGameStats: async (): Promise<GameStats[]> => {
    try {
      const response = await api.get('/admin/dashboard/game-stats');
      const data = response.data.data || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Failed to fetch game stats:', error);
      return [];
    }
  },

  getRevenueData: async (days: number = 30): Promise<RevenueData[]> => {
    try {
      const response = await api.get(`/admin/dashboard/revenue?days=${days}`);
      const data = response.data.data || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
      return [];
    }
  },
};

// =============================================================================
// USERS
// =============================================================================

export const userService = {
  getAll: async (
    page = 1,
    limit = 20,
    search?: string,
    filters?: { kycStatus?: string; riskLevel?: string; tier?: string }
  ): Promise<PaginatedResponse<User>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    if (filters?.kycStatus && filters.kycStatus !== 'ALL') params.append('kycStatus', filters.kycStatus);
    if (filters?.riskLevel && filters.riskLevel !== 'ALL') params.append('riskLevel', filters.riskLevel);
    if (filters?.tier && filters.tier !== 'ALL') params.append('tier', filters.tier);
    const response = await api.get(`/admin/users?${params}`);
    return response.data.data || response.data;
  },

  // Lightweight search for the global search popover (returns a slim list).
  search: async (q: string, limit = 5): Promise<Array<Pick<User, 'id' | 'username' | 'email'>>> => {
    const params = new URLSearchParams({ search: q, limit: String(limit) });
    const response = await api.get(`/admin/users/search?${params.toString()}`);
    const data = response.data.data ?? response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  },

  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data.data || response.data;
  },

  update: async (id: string, data: Partial<User>): Promise<User> => {
    const response = await api.patch(`/admin/users/${id}`, data);
    return response.data.data || response.data;
  },

  suspend: async (id: string, reason: string): Promise<void> => {
    await api.post(`/admin/users/${id}/suspend`, { reason });
  },

  unsuspend: async (id: string): Promise<void> => {
    await api.post(`/admin/users/${id}/unsuspend`);
  },

  ban: async (id: string, reason: string, duration?: number): Promise<void> => {
    await api.post(`/admin/users/${id}/ban`, { reason, duration });
  },

  unban: async (id: string): Promise<void> => {
    await api.delete(`/admin/users/${id}/ban`);
  },

  updateRole: async (id: string, role: string): Promise<void> => {
    await api.patch(`/admin/users/${id}/role`, { role });
  },
};

// =============================================================================
// GAMES (Sessions)
// =============================================================================

export const gameService = {
  getAll: async (
    page = 1,
    limit = 20,
    gameType?: GameType,
    status?: string,
    search?: string
  ): Promise<PaginatedResponse<GameSession>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (gameType) params.append('gameType', gameType);
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    const response = await api.get(`/admin/games/sessions?${params}`);
    return response.data.data || response.data;
  },

  getById: async (id: string): Promise<GameSession> => {
    const response = await api.get(`/admin/sessions/${id}/replay`);
    return response.data.data || response.data;
  },

  cancel: async (id: string, reason: string, refundPlayers?: boolean): Promise<void> => {
    await api.post(`/admin/games/sessions/${id}/cancel`, { reason, refundPlayers });
  },

  getActiveGames: async (): Promise<GameSession[]> => {
    const response = await api.get('/admin/sessions');
    const data = response.data.data || response.data;
    return data.sessions || data;
  },

  // Game configurations
  getConfigs: async () => {
    const response = await api.get('/admin/games');
    return response.data.data || response.data;
  },

  updateConfig: async (gameId: string, config: any) => {
    const response = await api.patch(`/admin/games/${gameId}`, config);
    return response.data.data || response.data;
  },
};

// =============================================================================
// TOURNAMENTS
// =============================================================================

export const tournamentService = {
  getAll: async (page = 1, limit = 20, status?: string): Promise<PaginatedResponse<Tournament>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.append('status', status);
    const response = await api.get(`/admin/tournaments?${params}`);
    return response.data.data || response.data;
  },

  getById: async (id: string): Promise<Tournament> => {
    const response = await api.get(`/tournaments/${id}`);
    return response.data.data || response.data;
  },

  create: async (data: Partial<Tournament>): Promise<Tournament> => {
    const response = await api.post('/tournaments', data);
    return response.data.data || response.data;
  },

  update: async (id: string, data: Partial<Tournament>): Promise<Tournament> => {
    const response = await api.patch(`/tournaments/${id}`, data);
    return response.data.data || response.data;
  },

  cancel: async (id: string, reason: string, refundEntries?: boolean): Promise<void> => {
    await api.post(`/admin/tournaments/${id}/cancel`, { reason, refundEntries });
  },

  start: async (id: string): Promise<void> => {
    await api.post(`/tournaments/${id}/start`);
  },

  complete: async (id: string): Promise<void> => {
    await api.post(`/admin/tournaments/${id}/complete`);
  },
};

// =============================================================================
// TRANSACTIONS
// =============================================================================

export const transactionService = {
  getAll: async (
    page = 1,
    limit = 20,
    type?: string,
    status?: string,
    search?: string
  ): Promise<PaginatedResponse<Transaction>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (type) params.append('type', type);
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    const response = await api.get(`/admin/transactions?${params}`);
    return response.data.data || response.data;
  },

  getById: async (id: string): Promise<Transaction> => {
    const response = await api.get(`/admin/transactions/${id}`);
    return response.data.data || response.data;
  },

  // Lightweight search for the global search popover.
  search: async (q: string, limit = 5): Promise<Transaction[]> => {
    const params = new URLSearchParams({ search: q, limit: String(limit) });
    const response = await api.get(`/admin/transactions?${params.toString()}`);
    const data = response.data.data ?? response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  },

  approve: async (id: string, notes?: string): Promise<void> => {
    await api.post(`/admin/transactions/${id}/approve`, { notes });
  },

  reject: async (id: string, reason: string): Promise<void> => {
    await api.post(`/admin/transactions/${id}/reject`, { reason });
  },
};

// =============================================================================
// REPORTS
// =============================================================================

export const reportService = {
  getAll: async (
    page = 1,
    limit = 20,
    status?: string
  ): Promise<PaginatedResponse<Report>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.append('status', status);
    const response = await api.get(`/admin/reports?${params}`);
    return response.data.data || response.data;
  },

  review: async (
    id: string,
    status: string,
    resolution: string,
    banUser?: boolean,
    banDuration?: number
  ): Promise<void> => {
    await api.post(`/admin/reports/${id}/review`, {
      status,
      resolution,
      banUser,
      banDuration,
    });
  },
};

// =============================================================================
// ANTI-CHEAT
// =============================================================================

export const antiCheatService = {
  getAll: async (
    page = 1,
    limit = 20,
    severity?: string,
    isReviewed?: boolean
  ): Promise<PaginatedResponse<AntiCheatEvent>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (severity) params.append('severity', severity);
    if (isReviewed !== undefined) params.append('isReviewed', String(isReviewed));
    const response = await api.get(`/admin/anticheat?${params}`);
    return response.data.data || response.data;
  },

  review: async (
    id: string,
    action: string,
    banUser?: boolean,
    banDuration?: number
  ): Promise<void> => {
    await api.post(`/admin/anticheat/${id}/review`, {
      action,
      banUser,
      banDuration,
    });
  },
};

// =============================================================================
// ADMIN ACTIONS (AUDIT LOG)
// =============================================================================

export const auditService = {
  getAll: async (
    page = 1,
    limit = 50,
    adminId?: string,
    action?: string
  ): Promise<PaginatedResponse<AdminAction>> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (adminId) params.append('adminId', adminId);
    if (action) params.append('action', action);
    // Prefer the new audit-logs endpoint (real immutable log). Fall back to the
    // legacy /admin/actions if the new route is unavailable.
    try {
      const response = await api.get(`/admin/audit-logs?${params}`);
      return response.data.data || response.data;
    } catch {
      const response = await api.get(`/admin/actions?${params}`);
      return response.data.data || response.data;
    }
  },
};

// =============================================================================
// SYSTEM CONFIG
// =============================================================================

export const configService = {
  getAll: async (): Promise<Record<string, any>> => {
    try {
      const response = await api.get('/admin/games');
      const data = response.data.data || response.data;
      // Return game configs as system config
      return data || {};
    } catch {
      // Return empty config if endpoint doesn't exist
      return {};
    }
  },

  get: async (key: string): Promise<any> => {
    try {
      const response = await api.get(`/admin/games/${key}`);
      return response.data.data || response.data;
    } catch {
      return null;
    }
  },

  update: async (key: string, value: any): Promise<void> => {
    await api.patch(`/admin/games/${key}`, value);
  },
};

// =============================================================================
// ANNOUNCEMENTS
// =============================================================================

export const announcementService = {
  create: async (
    title: string,
    message: string,
    type?: string,
    broadcast?: boolean
  ): Promise<void> => {
    await api.post('/admin/announcements', { title, message, type, broadcast });
  },
};

// =============================================================================
// LEADERBOARD
// =============================================================================

export const leaderboardService = {
  getGlobal: async (gameType: GameType, limit = 100): Promise<LeaderboardEntry[]> => {
    const response = await api.get(`/leaderboard/global/${gameType}?limit=${limit}`);
    const body = response.data.data || response.data;
    // Backend returns { leaderboard: [...], pagination, ... }
    return Array.isArray(body) ? body : (body?.leaderboard ?? body?.items ?? body?.data ?? []);
  },
};

// =============================================================================
// KYC
// =============================================================================

export type KycStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'NOT_SUBMITTED';
export type KycIdType = 'NATIONAL_ID' | 'PASSPORT' | 'DRIVERS_LICENSE';

export interface KycSubmission {
  id: string;
  userId: string;
  user: { id: string; username: string; email?: string; profilePicture?: string };
  idType: KycIdType;
  idNumber: string;
  frontImageUrl: string;
  backImageUrl?: string;
  selfieImageUrl: string;
  status: KycStatus;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  rejectionReason?: string;
  metadata?: {
    dateOfBirth?: string;
    fullName?: string;
    address?: string;
    country?: string;
  };
  history?: Array<{
    at: string;
    status: KycStatus;
    by?: string;
    note?: string;
  }>;
}

export const kycService = {
  getPending: async (page = 1, limit = 20): Promise<PaginatedResponse<KycSubmission>> => {
    const response = await api.get(`/admin/kyc/pending?page=${page}&limit=${limit}`);
    return response.data.data || response.data;
  },
  getAll: async (params: { page?: number; limit?: number; status?: KycStatus; search?: string } = {}): Promise<PaginatedResponse<KycSubmission>> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    const response = await api.get(`/admin/kyc?${qs.toString()}`);
    return response.data.data || response.data;
  },
  getById: async (id: string): Promise<KycSubmission> => {
    const response = await api.get(`/admin/kyc/${id}`);
    return response.data.data || response.data;
  },
  approve: async (id: string, note?: string): Promise<void> => {
    await api.post(`/admin/kyc/${id}/approve`, { note });
  },
  reject: async (id: string, reason: string): Promise<void> => {
    await api.post(`/admin/kyc/${id}/reject`, { reason });
  },
};

// =============================================================================
// PAYOUTS / PAYMENTS
// =============================================================================

export type PayoutStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
export type PayoutMethod = 'MPESA' | 'BANK' | 'VOOMA' | 'CARD' | 'WALLET';

export interface Payout {
  id: string;
  userId: string;
  user?: { id: string; username: string; email?: string };
  amount: number;
  currency: string;
  method: PayoutMethod;
  provider: string;
  destination: string;
  status: PayoutStatus;
  createdAt: string;
  processedAt?: string;
  completedAt?: string;
  failureReason?: string;
  reference?: string;
  logs?: Array<{ at: string; event: string; detail?: string; level: 'info' | 'warn' | 'error' }>;
  fees?: number;
  netAmount?: number;
}

export const payoutService = {
  getAll: async (params: { page?: number; limit?: number; status?: PayoutStatus; search?: string } = {}): Promise<PaginatedResponse<Payout>> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    const response = await api.get(`/admin/payouts?${qs.toString()}`);
    return response.data.data || response.data;
  },
  getById: async (id: string): Promise<Payout> => {
    const response = await api.get(`/admin/payouts/${id}`);
    return response.data.data || response.data;
  },
  retry: async (id: string): Promise<void> => {
    await api.post(`/admin/payouts/${id}/retry`);
  },
  manualApprove: async (id: string, confirmationCode: string, note?: string): Promise<void> => {
    await api.post(`/admin/payouts/${id}/manual-approve`, { confirmationCode, note });
  },
  cancel: async (id: string, reason: string): Promise<void> => {
    await api.post(`/admin/payouts/${id}/cancel`, { reason });
  },
  // Create a manual payout on behalf of a user — backend writes a WalletTransaction
  // of type WITHDRAWAL. Method+reason are stored inline so the list projection
  // picks up the correct channel badge.
  create: async (payload: {
    userId: string;
    amount: number;
    method: 'MPESA' | 'CARD' | 'BANK';
    phoneNumber?: string;
    reason: string;
    reference?: string;
  }): Promise<Payout> => {
    const response = await api.post('/admin/payouts', payload);
    return response.data.data || response.data;
  },
};

export interface ReconciliationRun {
  id: string;
  startedAt: string;
  completedAt?: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  totalChecked: number;
  mismatchCount: number;
  resolvedCount: number;
  triggeredBy: string;
  notes?: string;
}

export interface ReconciliationMismatch {
  id: string;
  runId: string;
  kind: 'AMOUNT' | 'STATUS' | 'MISSING_LOCAL' | 'MISSING_REMOTE';
  localRef?: string;
  remoteRef?: string;
  localAmount?: number;
  remoteAmount?: number;
  localStatus?: string;
  remoteStatus?: string;
  userId?: string;
  detectedAt: string;
  resolved: boolean;
  resolutionNote?: string;
}

export const reconciliationService = {
  getRuns: async (page = 1, limit = 20): Promise<PaginatedResponse<ReconciliationRun>> => {
    const response = await api.get(`/admin/reconciliation/runs?page=${page}&limit=${limit}`);
    return response.data.data || response.data;
  },
  getRun: async (id: string): Promise<ReconciliationRun> => {
    const response = await api.get(`/admin/reconciliation/runs/${id}`);
    return response.data.data || response.data;
  },
  getMismatches: async (runId: string, onlyUnresolved = false): Promise<ReconciliationMismatch[]> => {
    const response = await api.get(`/admin/reconciliation/runs/${runId}/mismatches?unresolved=${onlyUnresolved}`);
    const data = response.data.data || response.data;
    const raw = Array.isArray(data) ? data : (data?.items || data?.mismatches || []);
    // Backend keys differ from the UI's local/remote model. Normalise so the
    // page can render `localRef`/`remoteRef`/`localAmount`/`remoteAmount`.
    return (Array.isArray(raw) ? raw : []).map((m: any) => ({
      id: String(m?.id ?? ''),
      runId: String(m?.runId ?? runId),
      kind: (m?.kind ?? m?.reason ?? 'STATUS') as ReconciliationMismatch['kind'],
      localRef: m?.localRef ?? m?.providerRef ?? undefined,
      remoteRef: m?.remoteRef ?? m?.providerRef ?? undefined,
      localAmount: m?.localAmount ?? m?.expectedAmount ?? undefined,
      remoteAmount: m?.remoteAmount ?? m?.actualAmount ?? undefined,
      localStatus: m?.localStatus ?? undefined,
      remoteStatus: m?.remoteStatus ?? undefined,
      userId: m?.userId ?? undefined,
      detectedAt: String(m?.detectedAt ?? m?.createdAt ?? ''),
      resolved: Boolean(m?.resolved),
      resolutionNote: m?.resolutionNote ?? undefined,
    }));
  },
  triggerRun: async (scope?: 'ALL' | 'PAYOUTS' | 'DEPOSITS'): Promise<ReconciliationRun> => {
    const response = await api.post('/admin/reconciliation/run', { scope: scope || 'ALL' });
    return response.data.data || response.data;
  },
  resolveMismatch: async (id: string, note: string): Promise<void> => {
    await api.post(`/admin/reconciliation/mismatches/${id}/resolve`, { note });
  },
};

// =============================================================================
// FRAUD & RISK
// =============================================================================

export type RiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type AlertSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface RiskUser {
  userId: string;
  username: string;
  riskScore: number;
  riskLevel: RiskLevel;
  flags: string[];
  lastActivity: string;
  activeAlerts: number;
  totalFlagged: number;
}

export interface FraudAlert {
  id: string;
  userId: string;
  username?: string;
  kind: string;
  severity: AlertSeverity;
  title: string;
  description: string;
  createdAt: string;
  resolvedAt?: string;
  metadata?: Record<string, unknown>;
}

export interface RiskDevice {
  id: string;
  userId: string;
  deviceId: string;
  platform: string;
  model?: string;
  firstSeenAt: string;
  lastSeenAt: string;
  ipAddress?: string;
  geolocation?: { country?: string; region?: string; city?: string; lat?: number; lon?: number };
  trusted: boolean;
  flagged: boolean;
}

export interface IpLoginEvent {
  id: string;
  userId: string;
  ipAddress: string;
  country?: string;
  city?: string;
  userAgent?: string;
  at: string;
  successful: boolean;
}

export interface RiskOverview {
  highRiskUsers: number;
  activeAlerts: number;
  flagged24h: number;
  distribution: Array<{ bucket: string; count: number }>;
}

export const fraudService = {
  getOverview: async (): Promise<RiskOverview> => {
    const response = await api.get('/admin/risk/overview');
    return response.data.data || response.data;
  },
  getRiskUsers: async (params: { page?: number; limit?: number; level?: RiskLevel } = {}): Promise<PaginatedResponse<RiskUser>> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.level) qs.set('level', params.level);
    const response = await api.get(`/admin/risk/users?${qs.toString()}`);
    return response.data.data || response.data;
  },
  getUserRisk: async (userId: string): Promise<{
    user: RiskUser;
    devices: RiskDevice[];
    ipHistory: IpLoginEvent[];
    events: FraudAlert[];
  }> => {
    const response = await api.get(`/admin/risk/user/${userId}`);
    return response.data.data || response.data;
  },
  getAlerts: async (cursor?: string, limit = 25): Promise<{ items: FraudAlert[]; nextCursor?: string }> => {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    qs.set('limit', String(limit));
    const response = await api.get(`/admin/risk/alerts?${qs.toString()}`);
    return response.data.data || response.data;
  },
  flagUser: async (userId: string, reason: string): Promise<void> => {
    await api.post(`/admin/risk/user/${userId}/flag`, { reason });
  },
  unflagUser: async (userId: string, note?: string): Promise<void> => {
    await api.post(`/admin/risk/user/${userId}/unflag`, { note });
  },
  resolveAlert: async (alertId: string, note: string): Promise<void> => {
    await api.post(`/admin/risk/alerts/${alertId}/resolve`, { note });
  },
};

// =============================================================================
// SUPPORT TICKETING
// =============================================================================

export type TicketStatus = 'OPEN' | 'IN_PROGRESS' | 'ESCALATED' | 'RESOLVED' | 'CLOSED';
export type TicketPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type TicketCategory = 'BILLING' | 'ACCOUNT' | 'TECHNICAL' | 'GAMEPLAY' | 'KYC' | 'OTHER';

export interface TicketMessage {
  id: string;
  ticketId: string;
  author: { id: string; name: string; type: 'user' | 'admin' | 'system' };
  body: string;
  createdAt: string;
  attachments?: Array<{ url: string; name: string; mime: string }>;
}

export interface SupportTicket {
  id: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  userId: string;
  user: { id: string; username: string; email?: string; profilePicture?: string };
  assignedToId?: string;
  assignedTo?: { id: string; username: string };
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string;
  unreadForAdmin?: number;
  messages?: TicketMessage[];
}

export const supportService = {
  getAll: async (params: { page?: number; limit?: number; status?: TicketStatus; category?: TicketCategory; search?: string } = {}): Promise<PaginatedResponse<SupportTicket>> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.status) qs.set('status', params.status);
    if (params.category) qs.set('category', params.category);
    if (params.search) qs.set('search', params.search);
    const response = await api.get(`/admin/tickets?${qs.toString()}`);
    return response.data.data || response.data;
  },
  getById: async (id: string): Promise<SupportTicket> => {
    const response = await api.get(`/admin/tickets/${id}`);
    return response.data.data || response.data;
  },
  // Lightweight search for the global search popover.
  search: async (q: string, limit = 5): Promise<SupportTicket[]> => {
    const params = new URLSearchParams({ search: q, limit: String(limit) });
    const response = await api.get(`/admin/tickets?${params.toString()}`);
    const data = response.data.data ?? response.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  },
  reply: async (id: string, body: string): Promise<TicketMessage> => {
    const response = await api.post(`/admin/tickets/${id}/reply`, { body });
    return response.data.data || response.data;
  },
  escalate: async (id: string, note: string): Promise<void> => {
    await api.post(`/admin/tickets/${id}/escalate`, { note });
  },
  close: async (id: string, resolution: string): Promise<void> => {
    await api.post(`/admin/tickets/${id}/close`, { resolution });
  },
  assign: async (id: string, adminId: string): Promise<void> => {
    await api.post(`/admin/tickets/${id}/assign`, { adminId });
  },
  listAdmins: async (): Promise<Array<{ id: string; username: string; role: string }>> => {
    const response = await api.get('/admin/admins');
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : data.items || [];
  },
  // Admin-authored ticket: opens the thread with an initial admin message.
  create: async (payload: {
    userId: string;
    subject: string;
    category: TicketCategory;
    priority: TicketPriority;
    initialMessage: string;
    authorId?: string;
  }): Promise<SupportTicket> => {
    const response = await api.post('/admin/tickets', payload);
    return response.data.data || response.data;
  },
};

// =============================================================================
// RBAC / ROLES
// =============================================================================

// v2 admin RBAC (FR-ADM-01): five canonical roles.
export type AdminRole = 'SUPER_ADMIN' | 'FINANCE_ADMIN' | 'GAME_ADMIN' | 'RISK_ANALYST' | 'SUPPORT_AGENT';

/** All five roles in display order. */
export const ADMIN_ROLES: AdminRole[] = ['SUPER_ADMIN', 'FINANCE_ADMIN', 'GAME_ADMIN', 'RISK_ANALYST', 'SUPPORT_AGENT'];

export interface RolePermissions {
  id?: string;
  role: AdminRole;
  name?: string;
  description?: string | null;
  permissions: Record<string, boolean>;
  isSystem?: boolean;
}

export const rolesService = {
  getAll: async (): Promise<RolePermissions[]> => {
    const response = await api.get('/admin/roles');
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : data.items || [];
  },
  update: async (role: AdminRole, permissions: Record<string, boolean>): Promise<void> => {
    await api.patch(`/admin/roles/${role}`, { permissions });
  },
  create: async (payload: { key: string; name: string; description?: string; permissions?: Record<string, boolean> }): Promise<RolePermissions> => {
    const response = await api.post('/admin/roles', payload);
    return response.data.data || response.data;
  },
  remove: async (roleOrId: string): Promise<void> => {
    await api.delete(`/admin/roles/${roleOrId}`);
  },
  getPermissionKeys: async (): Promise<string[]> => {
    const response = await api.get('/admin/permissions');
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : data.items || [];
  },
};

// =============================================================================
// FEATURE FLAGS
// =============================================================================

export type Env = 'dev' | 'staging' | 'prod';
export type Region = 'KE' | 'TZ' | 'UG';

export interface FeatureFlag {
  key: string;
  description: string;
  envs: Record<Env, boolean>;
  regions: Record<Region, boolean>;
  updatedAt: string;
  updatedBy?: string;
}

export const featureFlagService = {
  getAll: async (): Promise<FeatureFlag[]> => {
    const response = await api.get('/admin/feature-flags');
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : data.items || [];
  },
  update: async (key: string, patch: Partial<Pick<FeatureFlag, 'envs' | 'regions' | 'description'>> & { category?: string }): Promise<FeatureFlag> => {
    const response = await api.patch(`/admin/feature-flags/${key}`, patch);
    return response.data.data || response.data;
  },
  create: async (payload: {
    key: string;
    description?: string;
    category?: string;
    enabled?: { dev?: boolean; staging?: boolean; prod?: boolean };
    regions?: { KE?: boolean; TZ?: boolean; UG?: boolean };
  }): Promise<FeatureFlag> => {
    const response = await api.post('/admin/feature-flags', payload);
    return response.data.data || response.data;
  },
  remove: async (key: string): Promise<void> => {
    await api.delete(`/admin/feature-flags/${encodeURIComponent(key)}`);
  },
};

// =============================================================================
// REPORTS / STATS
// =============================================================================

export interface OverviewStats {
  users: { dau: number; mau: number; newToday: number; total: number };
  revenue: { today: number; week: number; month: number; total: number };
  pending: { kyc: number; payouts: number; tickets: number; disputes: number };
  games: { activeSessions: number; today: number };
}

export interface RevenuePoint { date: string; revenue: number; transactions: number }
export interface UserGrowthPoint { date: string; newUsers: number; activeUsers: number }
export interface GamePerformance { gameType: string; plays: number; revenue: number; avgStake: number }
export interface CohortCell { cohort: string; period: number; retention: number }

export const statsService = {
  overview: async (): Promise<OverviewStats> => {
    const response = await api.get('/admin/stats/overview');
    return response.data.data || response.data;
  },
  revenueSeries: async (days = 30): Promise<RevenuePoint[]> => {
    const response = await api.get(`/admin/stats/revenue?days=${days}`);
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : [];
  },
  userGrowth: async (days = 30): Promise<UserGrowthPoint[]> => {
    const response = await api.get(`/admin/stats/user-growth?days=${days}`);
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : [];
  },
  gamePerformance: async (): Promise<GamePerformance[]> => {
    const response = await api.get('/admin/stats/game-performance');
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : [];
  },
  retentionCohorts: async (): Promise<CohortCell[]> => {
    const response = await api.get('/admin/stats/retention');
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : [];
  },
  scheduleReport: async (payload: { name: string; email: string; cron: string; kind: string }): Promise<void> => {
    await api.post('/admin/reports/schedule', payload);
  },
};

// =============================================================================
// DISPUTES (game-level)
// =============================================================================

export type DisputeStatus = 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'REJECTED';

export interface Dispute {
  id: string;
  sessionId: string;
  reporterId: string;
  reporter?: { id: string; username: string };
  reason: string;
  status: DisputeStatus;
  createdAt: string;
  resolvedAt?: string;
  resolution?: string;
  gameType?: string;
}

export const disputeService = {
  getAll: async (params: { page?: number; limit?: number; status?: DisputeStatus } = {}): Promise<PaginatedResponse<Dispute>> => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.status) qs.set('status', params.status);
    const response = await api.get(`/admin/disputes?${qs.toString()}`);
    return response.data.data || response.data;
  },
  resolve: async (id: string, resolution: string, refund?: boolean): Promise<void> => {
    await api.post(`/admin/disputes/${id}/resolve`, { resolution, refund });
  },
};

// =============================================================================
// GLOBAL SEARCH
// =============================================================================

export interface GlobalSearchResult {
  users: Array<{ id: string; username: string; email?: string }>;
  transactions: Array<{ id: string; reference?: string; amount: number }>;
  tickets: Array<{ id: string; subject: string; status: string }>;
}

export const searchService = {
  global: async (q: string): Promise<GlobalSearchResult> => {
    const response = await api.get(`/admin/search?q=${encodeURIComponent(q)}`);
    return response.data.data || response.data;
  },
};

// =============================================================================
// USER DETAIL (enriched)
// =============================================================================

export interface UserActivity {
  id: string;
  kind: string;
  summary: string;
  at: string;
  metadata?: Record<string, unknown>;
}

export interface UserWallet {
  userId: string;
  balance: number;
  currency: string;
  pending: number;
  lifetimeDeposits: number;
  lifetimeWithdrawals: number;
  recentTransactions: Transaction[];
}

export interface CoinBalance {
  balance: number;
  locked: number;
  available: number;
}

export const userDetailService = {
  getWallet: async (userId: string): Promise<UserWallet> => {
    const response = await api.get(`/admin/users/${userId}/wallet`);
    return response.data.data || response.data;
  },
  // Closed coin economy — the balance that stakes/settlements and admin funding
  // actually move. (`/wallet` above is not wired on the backend.)
  getCoins: async (userId: string): Promise<CoinBalance> => {
    const response = await api.get(`/admin/users/${userId}/coins`);
    return response.data.data || response.data;
  },
  getCoinHistory: async (userId: string): Promise<Array<Record<string, unknown>>> => {
    const response = await api.get(`/admin/users/${userId}/coins/history`);
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : data.items || [];
  },
  // Fund a player's wallet (SUPER_ADMIN). Powers the "Fund wallet" dashboard action.
  creditCoins: async (userId: string, amount: number, reason: string): Promise<CoinBalance> => {
    const response = await api.post(`/admin/users/${userId}/coins/credit`, { amount, reason });
    return response.data.data || response.data;
  },
  getActivity: async (userId: string, page = 1, limit = 30): Promise<PaginatedResponse<UserActivity>> => {
    const response = await api.get(`/admin/users/${userId}/activity?page=${page}&limit=${limit}`);
    return response.data.data || response.data;
  },
  getDevices: async (userId: string): Promise<RiskDevice[]> => {
    const response = await api.get(`/admin/users/${userId}/devices`);
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : data.items || [];
  },
  forceLogout: async (userId: string): Promise<void> => {
    await api.post(`/admin/users/${userId}/force-logout`);
  },
};

export default api;
